export const FORECAST_CLOCK_TICK_MS = 60_000;
export const FORECAST_REFRESH_INTERVAL_MS = 15 * 60_000;

const DUPLICATE_REFRESH_WINDOW_MS = 2_000;

export type ForecastRefreshReason =
  | "interval"
  | "manual"
  | "return"
  | "site_day_rollover";

export interface ForecastRefreshEnvironment {
  now: () => number;
  isVisible: () => boolean;
  setInterval: (callback: () => void, delayMs: number) => number;
  clearInterval: (timerId: number) => void;
  subscribeVisibility: (callback: () => void) => () => void;
  subscribeFocus: (callback: () => void) => () => void;
}

interface ForecastRefreshScheduleOptions {
  environment: ForecastRefreshEnvironment;
  timezone: string;
  initialRefreshAtMs: number;
  isRefreshInFlight: () => boolean;
  onClock: (nowMs: number) => void;
  onRefresh: (reason: ForecastRefreshReason) => void;
  refreshIntervalMs?: number;
}

export interface ForecastRefreshSchedule {
  refreshNow: () => boolean;
  stop: () => void;
}

export function siteLocalDateKey(nowMs: number, timezone: string): string | null {
  const date = new Date(nowMs);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    if (!values.year || !values.month || !values.day) {
      return null;
    }
    return `${values.year}-${values.month}-${values.day}`;
  } catch (error) {
    if (error instanceof RangeError) {
      return null;
    }
    throw error;
  }
}

/**
 * Coordinate the page clock and foreground refresh triggers without owning the
 * request itself. The page remains responsible for aborting and sequencing
 * requests; this scheduler only decides when another request is due.
 */
export function startForecastRefreshSchedule(
  options: ForecastRefreshScheduleOptions
): ForecastRefreshSchedule {
  const { environment } = options;
  const refreshIntervalMs =
    options.refreshIntervalMs ?? FORECAST_REFRESH_INTERVAL_MS;
  let lastRefreshAtMs = options.initialRefreshAtMs;
  let lastTriggeredAtMs = Number.NEGATIVE_INFINITY;
  let localDateKey = siteLocalDateKey(environment.now(), options.timezone);
  let siteDayRefreshPending = false;
  let stopped = false;

  const trigger = (reason: ForecastRefreshReason, nowMs: number): boolean => {
    if (
      stopped ||
      options.isRefreshInFlight() ||
      nowMs - lastTriggeredAtMs < DUPLICATE_REFRESH_WINDOW_MS
    ) {
      return false;
    }
    lastTriggeredAtMs = nowMs;
    lastRefreshAtMs = nowMs;
    siteDayRefreshPending = false;
    options.onRefresh(reason);
    return true;
  };

  const updateClockAndSiteDay = (nowMs: number): void => {
    options.onClock(nowMs);
    const nextLocalDateKey = siteLocalDateKey(nowMs, options.timezone);
    const siteDayRolledOver =
      localDateKey !== null &&
      nextLocalDateKey !== null &&
      nextLocalDateKey !== localDateKey;
    localDateKey = nextLocalDateKey;
    siteDayRefreshPending ||= siteDayRolledOver;
  };

  const tick = () => {
    const nowMs = environment.now();
    updateClockAndSiteDay(nowMs);
    if (!environment.isVisible()) {
      return;
    }
    if (siteDayRefreshPending && trigger("site_day_rollover", nowMs)) {
      return;
    }
    if (nowMs - lastRefreshAtMs >= refreshIntervalMs) {
      trigger("interval", nowMs);
    }
  };

  const refreshOnReturn = () => {
    if (!environment.isVisible()) {
      return;
    }
    const nowMs = environment.now();
    updateClockAndSiteDay(nowMs);
    trigger(siteDayRefreshPending ? "site_day_rollover" : "return", nowMs);
  };

  const timerId = environment.setInterval(tick, FORECAST_CLOCK_TICK_MS);
  const unsubscribeVisibility = environment.subscribeVisibility(refreshOnReturn);
  const unsubscribeFocus = environment.subscribeFocus(refreshOnReturn);

  return {
    refreshNow: () => {
      const nowMs = environment.now();
      updateClockAndSiteDay(nowMs);
      return trigger("manual", nowMs);
    },
    stop: () => {
      if (stopped) {
        return;
      }
      stopped = true;
      environment.clearInterval(timerId);
      unsubscribeVisibility();
      unsubscribeFocus();
    }
  };
}

/** A late response may only update state while it is still the latest request. */
export class ForecastRequestGate {
  private latestRequestId = 0;

  begin(): number {
    this.latestRequestId += 1;
    return this.latestRequestId;
  }

  isCurrent(requestId: number): boolean {
    return requestId === this.latestRequestId;
  }

  invalidate(requestId: number): void {
    if (this.isCurrent(requestId)) {
      this.latestRequestId += 1;
    }
  }
}
